export const locales = ['en', 'zh', 'ar'] as const

export type Locale = (typeof locales)[number]

export const defaultLocale: Locale = 'en'

/** Locales whose script runs right-to-left. */
const rtlLocales: ReadonlySet<Locale> = new Set<Locale>(['ar'])

/** Document direction for a locale — drives `<html dir>`. */
export function localeDirection(locale: string): 'rtl' | 'ltr' {
  return rtlLocales.has(locale as Locale) ? 'rtl' : 'ltr'
}

/**
 * Pick the supported locale with the highest effective q-value from an
 * Accept-Language header, or null when none matches.
 *
 * Used as the first-visit default before the user ever touches a language
 * switcher (no NEXT_LOCALE cookie yet). Each comma-separated entry may carry
 * a `;q=<weight>` parameter (default 1.0); entries with `q=0` are explicitly
 * unacceptable and excluded, malformed or out-of-range weights fall back to
 * 1.0, and ties keep header order. Region subtags collapse to their base
 * language (`zh-CN` → `zh`, `en-GB` → `en`,
 * `ar-SA` → `ar`); unsupported tags are skipped.
 */
export function matchAcceptLanguage(
  header: string | null | undefined,
): Locale | null {
  if (!header) return null
  const candidates: { tag: string; q: number }[] = []
  for (const part of header.split(',')) {
    const [rawTag, ...params] = part.split(';')
    const tag = rawTag?.trim().toLowerCase()
    if (!tag) continue
    let q = 1.0
    for (const param of params) {
      const [key, value] = param.split('=')
      if (key?.trim().toLowerCase() !== 'q') continue
      // An empty weight (`;q=` or bare `;q`) is malformed, not an explicit
      // q=0 exclusion — Number('') would coerce to 0, so guard it first.
      const trimmed = value?.trim() ?? ''
      if (trimmed === '') break
      const parsed = Number(trimmed)
      q = Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 1.0
      break
    }
    if (q === 0) continue
    candidates.push({ tag, q })
  }
  // Array.prototype.sort is stable, so equal-q entries keep header order.
  candidates.sort((a, b) => b.q - a.q)
  for (const { tag } of candidates) {
    const base = tag.split('-')[0]
    const match = locales.find(l => l === tag || l === base)
    if (match) return match
  }
  return null
}
